/* Credit account application page - sections/credit-account-application-page.liquid.
   Needs assets/rental-signing-common.js (window.MCSRentalSigning) first.

   Two modes:
   - Standalone (no token): a blank application any customer can be sent.
   - Rental (?t=<token>, from the rental agreement's Account / EFT link):
     prefilled from GET {base}/rental-agreement/api/prefill exactly as the
     former modal was. A 404 or already-signed token falls back to
     standalone - the application is still useful.
   - Staff link (?t=ca.<token>, from Workspace's Create account application):
     prefilled from GET {base}/credit-account-application/api/prefill, whose
     terms wording replaces every [data-ca-terms] text. The submit carries
     the displayed terms label; the server refuses (409) a page whose terms
     are out of date. A 404 falls back to standalone.
   The token lives under its own sessionStorage key, set only from a ?t= in
   the URL (then stripped), so a stale rental token never attaches to a later
   standalone application. Validation is the former modal's, and the submit
   is POST {base}/credit-account-application/api/submit
   { "token": "<token or ''>", "account_application": {...}, "hp_field": "..." }
   with account_application in the shape the modal built (accountApp.data). */
(function () {
  'use strict';

  var S = window.MCSRentalSigning;
  if (!S) return;
  var str = S.str;
  var joinList = S.joinList;
  var AGREEMENT_PAGE = '/pages/rental-agreement';
  var TOKEN_STORE_KEY = 'mcs-credit-application-token';
  /* Staff links; '.' never occurs in a rental agreement token. */
  var INVITE_PREFIX = 'ca.';

  var init = function (root) {
    var config = S.readConfig(root);
    var prefillUrl = S.endpoint(config.baseUrl, config.prefillPath, 'rental-agreement/api/prefill');
    var submitUrl = S.endpoint(config.baseUrl, config.submitPath, 'credit-account-application/api/submit');
    var inviteUrl = S.endpoint(config.baseUrl, config.invitePrefillPath, 'credit-account-application/api/prefill');

    var q = function (selector, scope) { return (scope || root).querySelector(selector); };
    var qa = function (selector, scope) { return Array.prototype.slice.call((scope || root).querySelectorAll(selector)); };

    var intro = q('[data-ra-intro]');
    var showState = S.createStates(root, function (name) {
      if (intro) intro.hidden = name !== 'form';
    });

    var form = q('[data-ca-form]');
    if (!form) return;
    var status = S.statusBox(q('[data-ca-status]'));
    var submitButton = q('[data-ca-submit]');
    var submitText = q('[data-ca-submit-text]');
    var submitLabel = submitText ? submitText.textContent : 'Submit Application';
    var submitting = false;
    var token = S.readToken(TOKEN_STORE_KEY);
    var isInvite = token.indexOf(INVITE_PREFIX) === 0;
    /* The payment terms the page states (and the applicant signs to). */
    var termsLabel = str((q('[data-ca-terms="label"]') || {}).textContent) || '7 days from end of invoice month';

    /* Rental vs standalone wording (eyebrow + intro) and the rental-only
       "replaces your earlier application" note. */
    var setMode = function (mode) {
      root.setAttribute('data-ca-mode', mode);
      qa('[data-ca-copy]').forEach(function (el) {
        el.hidden = el.getAttribute('data-ca-copy') !== mode;
      });
      if (mode !== 'rental') {
        var resubmit = q('[data-ca-resubmit]');
        if (resubmit) resubmit.hidden = true;
      }
      var agreementLink = q('[data-ca-agreement-link]');
      if (agreementLink) agreementLink.href = AGREEMENT_PAGE + (token ? '?t=' + encodeURIComponent(token) : '');
    };

    var val = function (name) {
      var field = form.elements[name];
      return field ? str(field.value) : '';
    };
    var setField = function (name, value) {
      var field = form.elements[name];
      if (field) field.value = str(value);
    };
    var answer = function (name) {
      var checked = form.querySelector('input[name="' + name + '"]:checked');
      return checked ? checked.value : '';
    };

    /* Yes/No reveals (separate delivery address / accounts contact). */
    var syncReveals = function () {
      qa('[data-ca-show-when]', form).forEach(function (block) {
        block.hidden = answer(block.getAttribute('data-ca-show-when')) !== 'yes';
      });
    };
    qa('input[type="radio"]', form).forEach(function (radio) { radio.addEventListener('change', syncReveals); });

    /* Signature pad (sized once the document is visible). */
    var sigCanvas = q('[data-ca-sig]');
    var sig = { drawn: false };
    var pad = null;
    var ensurePad = function () {
      if (pad && pad.ready) return;
      pad = S.initSignaturePad(sigCanvas, sig, 140);
    };
    var sigClear = q('[data-ca-sig-clear]');
    if (sigClear) sigClear.addEventListener('click', function () { if (pad) pad.clear(); });

    var showForm = function () {
      var today = q('[data-ca-today]');
      if (today) today.textContent = S.todayLong();
      syncReveals();
      showState('form');
      ensurePad();
    };

    /* Blank form, generic wording, no token attached to the submission. */
    var startStandalone = function () {
      token = '';
      isInvite = false;
      S.forgetToken(TOKEN_STORE_KEY);
      setMode('standalone');
      showForm();
    };

    /* A staff link's terms wording in every place the page states them. */
    var applyTerms = function (terms) {
      if (!terms || !str(terms.label)) return;
      qa('[data-ca-terms]').forEach(function (el) {
        var text = str(terms[el.getAttribute('data-ca-terms')]);
        if (text) el.textContent = text;
      });
      termsLabel = str(terms.label);
    };

    /* ---------- Rental prefill (same sources as the former modal) ---------- */
    var populate = function (result) {
      var prefill = result.prefill || {};
      var first = str(prefill.first_name);
      var last = str(prefill.last_name);
      var name = str(prefill.contact_name) || (first + ' ' + last).trim();
      setField('abn', prefill.abn);
      setField('trading_name', str(prefill.trading_name) || str(prefill.business_name));
      setField('postal_line1', prefill.street_address);
      setField('postal_suburb', prefill.city);
      setField('postal_state', prefill.state);
      setField('postal_postcode', prefill.postcode);
      setField('contact_name', name);
      setField('contact_phone', prefill.phone);
      setField('contact_email', prefill.email);
      setField('full_name', name);
      var submitted = result.account_application_submitted === true || prefill.account_application_submitted === true;
      var resubmit = q('[data-ca-resubmit]');
      if (resubmit) resubmit.hidden = !submitted;
    };

    var loadPrefill = function () {
      setMode('rental');
      showState('loading');
      S.fetchJson(prefillUrl + '?t=' + encodeURIComponent(token), {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      }).then(function (reply) {
        var response = reply.response;
        var result = reply.result;
        if (response.ok && result.success === true && result.status !== 'signed') {
          populate(result);
          showForm();
          return;
        }
        /* Expired/invalid link or a rental already signed: still a useful
           application, just not a rental one. */
        if (response.status === 404 || (response.ok && result.status === 'signed')) {
          startStandalone();
          return;
        }
        if (response.status === 503) showState('disabled', true);
        else showState('error', true);
      }).catch(function () {
        showState('error', true);
      });
    };
    /* ---------- Staff link: chosen terms + company prefill ---------- */
    var loadInvite = function () {
      setMode('invite');
      showState('loading');
      S.fetchJson(inviteUrl + '?t=' + encodeURIComponent(token), {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      }).then(function (reply) {
        var response = reply.response;
        var result = reply.result;
        if (response.ok && result.success === true && result.terms) {
          applyTerms(result.terms);
          populate({ prefill: result.prefill || {} });
          showForm();
          return;
        }
        if (response.status === 404) {
          startStandalone();
          return;
        }
        if (response.status === 503) showState('disabled', true);
        else showState('error', true);
      }).catch(function () {
        showState('error', true);
      });
    };
    var retryButton = q('[data-ra-retry]');
    if (retryButton) retryButton.addEventListener('click', function () {
      if (isInvite && token) loadInvite();
      else loadPrefill();
    });

    /* ---------- Validation (the former modal's rules, verbatim) ---------- */
    var invalid = [];
    var mark = function (name) {
      var target = form.elements[name];
      if (target && target.length !== undefined && !target.tagName) {
        target = form.querySelector('[data-ca-group="' + name + '"]');
      }
      if (name === 'terms_agree') target = q('[data-ca-agree]');
      if (name === 'signature') target = q('[data-ca-sigpad]');
      if (target && invalid.indexOf(target) === -1) {
        target.setAttribute('aria-invalid', 'true');
        invalid.push(target);
      }
    };
    var clearMarks = function () {
      invalid.forEach(function (el) { el.removeAttribute('aria-invalid'); });
      invalid = [];
    };
    form.addEventListener('input', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      var holder = target.closest('[aria-invalid]');
      if (holder) holder.removeAttribute('aria-invalid');
    });
    form.addEventListener('change', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      var holder = target.closest('[aria-invalid]');
      if (holder) holder.removeAttribute('aria-invalid');
    });

    var validate = function () {
      clearMarks();
      var requiredFields = [
        ['abn', 'ABN'],
        ['trading_name', 'Trading Name'],
        ['type_of_business', 'Type of Business'],
        ['postal_line1', 'Address Line'],
        ['postal_suburb', 'Suburb'],
        ['postal_state', 'State'],
        ['postal_postcode', 'Post Code'],
        ['contact_name', 'On-site Contact Name'],
        ['contact_phone', 'On-site Contact Phone'],
        ['contact_email', 'On-site Contact Email'],
        ['employees', 'No. of Employees'],
        ['business_commenced', 'Date Business Commenced'],
        ['credit_limit', 'Value of Credit Limit Sought'],
        ['full_name', 'Full Name'],
        ['position_held', 'Position Held']
      ];
      var missing = [];
      requiredFields.forEach(function (pair) {
        if (!val(pair[0])) {
          missing.push(pair[1]);
          mark(pair[0]);
        }
      });
      [1, 2, 3].forEach(function (n) {
        var keys = ['company', 'contact', 'phone', 'email'].map(function (k) { return 'ref' + n + '_' + k; });
        var gaps = keys.filter(function (key) { return !val(key); });
        if (gaps.length) {
          missing.push('Reference ' + n);
          gaps.forEach(mark);
        }
      });
      var issues = [];
      if (!val('applicant_type')) {
        issues.push('select the applicant type');
        mark('applicant_type');
      }
      if (missing.length) issues.push('complete ' + joinList(missing));
      var check = function (name, ok, message) {
        if (val(name) && !ok(val(name))) {
          issues.push(message);
          mark(name);
        }
      };
      check('abn', S.isValidAbn, 'enter a valid 11-digit ABN');
      check('type_of_business', S.looksLikeName, 'describe the type of business');
      check('postal_suburb', S.looksLikeName, 'enter a real suburb');
      check('postal_postcode', function (v) { return /^\d{4}$/.test(v); }, 'enter a 4-digit post code');
      check('contact_name', S.looksLikeName, "enter the on-site contact's full name");
      check('contact_phone', S.looksLikePhone, 'enter a valid contact phone number');
      check('contact_email', S.looksLikeEmail, 'enter a valid contact email address');
      check('employees', function (v) { var n = Number(v); return isFinite(n) && n >= 1 && n <= 10000; }, 'enter a realistic number of employees');
      check('business_commenced', function (v) {
        var commenced = new Date(v + 'T00:00:00');
        return !isNaN(commenced.getTime()) && commenced.getFullYear() >= 1900 && commenced <= new Date();
      }, 'enter a valid (past) business commencement date');
      check('credit_limit', function (v) {
        var n = Number(String(v).replace(/[^0-9.]/g, ''));
        return isFinite(n) && n >= 100;
      }, 'enter a realistic credit limit (at least $100)');
      var applicantEmail = val('contact_email').toLowerCase();
      var refCompanies = [];
      [1, 2, 3].forEach(function (n) {
        var p = 'ref' + n + '_';
        var company = val(p + 'company');
        var contact = val(p + 'contact');
        var phone = val(p + 'phone');
        var email = val(p + 'email');
        if (!company && !contact && !phone && !email) return; /* reported as missing above */
        var problems = [];
        if (company && !S.looksLikeName(company)) { problems.push('company name'); mark(p + 'company'); }
        if (contact && !S.looksLikeName(contact)) { problems.push('contact name'); mark(p + 'contact'); }
        if (phone && !S.looksLikePhone(phone)) { problems.push('phone number'); mark(p + 'phone'); }
        if (email && !S.looksLikeEmail(email)) { problems.push('email address'); mark(p + 'email'); }
        if (problems.length) issues.push('enter a real ' + problems.join(', ') + ' for reference ' + n);
        /* The declaration requires references that are NOT associated
           entities - the applicant's own email is a giveaway. */
        if (email && applicantEmail && email.toLowerCase() === applicantEmail) {
          issues.push("use the reference's own email for reference " + n + ' (not yours)');
          mark(p + 'email');
        }
        if (company) refCompanies.push(company.toLowerCase());
      });
      var uniqueCompanies = refCompanies.filter(function (c, i) { return refCompanies.indexOf(c) === i; });
      if (refCompanies.length === 3 && uniqueCompanies.length < 3) {
        issues.push('provide three different trade reference companies');
      }
      check('full_name', S.looksLikeName, 'enter your full name in the declaration');
      check('position_held', S.looksLikeName, 'enter your position held');
      if (answer('delivery_separate') === 'yes' && !val('delivery_address')) {
        issues.push('enter the delivery address (or answer No)');
        mark('delivery_address');
      }
      if (answer('accounts_separate') === 'yes' && !val('accounts_name')) {
        issues.push('enter the accounts contact name (or answer No)');
        mark('accounts_name');
      }
      if (!answer('judgements')) { issues.push('answer the judgements question'); mark('judgements'); }
      if (!answer('criminal')) { issues.push('answer the criminal offence question'); mark('criminal'); }
      if (!answer('paymethod')) { issues.push('choose a payment method'); mark('paymethod'); }
      if (!sig.drawn) { issues.push('sign the application'); mark('signature'); }
      if (!form.elements.terms_agree || !form.elements.terms_agree.checked) { issues.push('agree to the terms of trade'); mark('terms_agree'); }
      return issues;
    };

    /* Exactly the object the modal built (accountApp.data). */
    var buildApplication = function () {
      var today = new Date();
      return {
        applicant_type: val('applicant_type'),
        abn: val('abn'),
        trading_name: val('trading_name'),
        type_of_business: val('type_of_business'),
        postal_address: {
          line1: val('postal_line1'),
          line2: val('postal_line2'),
          suburb: val('postal_suburb'),
          state: val('postal_state'),
          postcode: val('postal_postcode')
        },
        delivery_separate: answer('delivery_separate') || 'no',
        delivery_address: val('delivery_address'),
        onsite_contact: {
          name: val('contact_name'),
          phone: val('contact_phone'),
          email: val('contact_email')
        },
        accounts_separate: answer('accounts_separate') || 'no',
        accounts_contact: {
          name: val('accounts_name'),
          phone: val('accounts_phone'),
          email: val('accounts_email')
        },
        employees: val('employees'),
        business_commenced: val('business_commenced'),
        judgements_or_bankruptcy: answer('judgements'),
        criminal_offence: answer('criminal'),
        credit_limit_sought: val('credit_limit'),
        payment_terms: termsLabel,
        payment_method: answer('paymethod'),
        references: [1, 2, 3].map(function (n) {
          return {
            company: val('ref' + n + '_company'),
            contact: val('ref' + n + '_contact'),
            phone: val('ref' + n + '_phone'),
            email: val('ref' + n + '_email')
          };
        }),
        /* Same "<day> day of <Month> 20<yy>" the modal's date line produced. */
        acceptance: {
          full_name: val('full_name'),
          position_held: val('position_held'),
          date: today.getDate() + ' day of ' + today.toLocaleDateString('en-AU', { month: 'long' }) + ' 20' + String(today.getFullYear()).slice(2)
        },
        terms_of_trade_agreed: true,
        signature: sigCanvas && sigCanvas.toDataURL ? sigCanvas.toDataURL('image/png') : '',
        completed_at: today.toISOString()
      };
    };

    /* Honeypot: raw value, untrimmed - bots fill it, people never see it. */
    var hpValue = function () {
      var field = form.elements.hp_field;
      return field ? String(field.value || '') : '';
    };

    var setSubmitting = function (value) {
      submitting = value;
      if (submitButton) submitButton.disabled = value;
      if (submitText) submitText.textContent = value ? 'Submitting…' : submitLabel;
      form.setAttribute('aria-busy', value ? 'true' : 'false');
    };

    /* Server field keys (account_application paths) -> what to flag here. */
    var FIELD_ALIASES = {
      'postal_address': 'postal_line1', 'postal_address.line1': 'postal_line1', 'postal_address.suburb': 'postal_suburb',
      'postal_address.state': 'postal_state', 'postal_address.postcode': 'postal_postcode',
      'onsite_contact': 'contact_name', 'onsite_contact.name': 'contact_name', 'onsite_contact.phone': 'contact_phone',
      'onsite_contact.email': 'contact_email', 'accounts_contact.name': 'accounts_name',
      'acceptance.full_name': 'full_name', 'acceptance.position_held': 'position_held',
      'credit_limit_sought': 'credit_limit', 'judgements_or_bankruptcy': 'judgements', 'criminal_offence': 'criminal',
      'payment_method': 'paymethod', 'terms_of_trade_agreed': 'terms_agree', 'references': 'ref1_company'
    };

    var handleError = function (response, result) {
      if (response.status === 429 || result.error === 'rate_limited') {
        status.set('Too many attempts. Please wait a few minutes and try again, or call 1300 365 487.', 'error');
        return;
      }
      if (response.status === 503 || result.error === 'disabled') {
        status.set('Online applications are unavailable right now. Please call 1300 365 487 and we\'ll help you set up your account.', 'error');
        return;
      }
      if (response.status === 409 || result.error === 'terms_changed') {
        status.set(str(result.message) || 'The payment terms on this page are out of date. Please reload the page and sign again.', 'error');
        return;
      }
      if (response.status === 400) {
        var fields = result.fields && typeof result.fields === 'object' ? result.fields : {};
        var details = [];
        clearMarks();
        Object.keys(fields).forEach(function (key) {
          var message = str(fields[key]);
          if (message) details.push(message);
          mark(FIELD_ALIASES[key] || key);
        });
        status.set(str(result.message) || 'Some details are missing. Please check the application and try again.', 'error', details);
        return;
      }
      status.set('We couldn\'t submit your application. Please try again or call 1300 365 487.', 'error');
    };

    var showSuccess = function (result) {
      var linked = result.linked_to_rental === true;
      qa('[data-ca-success]').forEach(function (block) {
        block.hidden = block.getAttribute('data-ca-success') !== (linked ? 'rental' : 'standalone');
      });
      var reference = str(result.reference);
      var refWrap = q('[data-ca-reference-wrap]');
      var refEl = q('[data-ca-reference]');
      if (refEl) refEl.textContent = reference;
      if (refWrap) refWrap.hidden = !reference;
      showState('success', true);
    };

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (submitting) return;
      status.clear();
      var issues = validate();
      if (issues.length) {
        status.set('Please ' + joinList(issues) + '.', 'error');
        var first = invalid[0];
        if (first) {
          var focusTarget = first.matches && first.matches('input, select') ? first : first.querySelector('input, button, select');
          S.focusQuietly(focusTarget || first);
          if (first.scrollIntoView) first.scrollIntoView({ behavior: S.reduceMotion() ? 'auto' : 'smooth', block: 'center' });
        }
        return;
      }
      setSubmitting(true);
      status.set('Submitting your application…', 'info');
      S.fetchJson(submitUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ token: token || '', account_application: buildApplication(), hp_field: hpValue() })
      }).then(function (reply) {
        if (reply.response.ok && reply.result.success === true) {
          status.clear();
          showSuccess(reply.result);
          return;
        }
        handleError(reply.response, reply.result);
      }).catch(function () {
        status.set('We couldn\'t reach our server. Please check your connection and try again, or call 1300 365 487.', 'error');
      }).then(function () {
        setSubmitting(false);
      });
    });

    if (token && isInvite) loadInvite();
    else if (token) loadPrefill();
    else startStandalone();
  };

  Array.prototype.slice.call(document.querySelectorAll('[data-ra-root]')).forEach(init);
})();
